import jwtDecode from 'jwt-decode'
import { identifyUser } from '../../services/logRocket'

const state = {
  user: null,
  userInfo: null,
  appConfigFeatureFlags: null,
  bootstrapLoaded: false,
  onboardingState: null,
  opsEmail: null
}

const mutations = {
  SET_USER_INFO (state, info) {
    state.userInfo = info
  },
  SET_APPCONFIG_FEATURE_FLAGS (state, flags) {
    state.appConfigFeatureFlags = flags
  },
  SET_OPS_EMAIL (state, email) {
    state.opsEmail = email
  },
  SET_BOOTSTRAP_LOADED (state, value) {
    state.bootstrapLoaded = value
  },
  SET_ONBOARDING_STATE (state, value) {
    state.onboardingState = value
  }
}

const actions = {
  async bootstrapCall ({ rootState, state, commit }) {
    if (state.bootstrapLoaded) { return }
    const info = await rootState.api.dataHub.getInvestorInfo()
    commit('SET_USER_INFO', info)

    const appConfigfeatureFlags = (await rootState.api.dataHub.appConfigFeatureFlags()).flags
    commit('SET_APPCONFIG_FEATURE_FLAGS', appConfigfeatureFlags)

    const metadata = await rootState.api.dataHub.getMetadata('operations_email')
    commit('SET_OPS_EMAIL', metadata?.value || null)

    commit('SET_BOOTSTRAP_LOADED', true)
  },
  async identifyLogRocketUser ({ rootState, state }) {
    const token = rootState.api.auth.localStorageService.getAccessToken()
    if (state.userInfo && token) {
      const decodeToken = jwtDecode(token)
      const clientId = decodeToken.client_id
      await identifyUser({
        client_id: clientId,
        name: `${state.userInfo.investor_first_name} ${state.userInfo.investor_last_name}`,
        email: state.userInfo.investor_email,
        firm: rootState?.context?.firm
      })
    }
  },
  resetSurveyStatus({ commit }) {
    commit('SET_ONBOARDING_STATE', null)
  },
  async getSurveyStatus ({ rootState, _state, commit }) {
    const onboardingState = await rootState.api.dataCapture.fetchDataCaptureStatus()
    commit('SET_ONBOARDING_STATE', onboardingState)
  },
  clearUserInfo ({ commit }) {
    commit('SET_USER_INFO', null)
    commit('SET_FEATURE_FLAGS', null)
    commit('SET_BOOTSTRAP_LOADED', false)
    commit('SET_ONBOARDING_STATE', null)
    commit('SET_OPS_EMAIL', null)
  },
  clearAccessToken ({ rootState }) {
    rootState.api.auth.localStorageService.clearToken()
    rootState.api.auth.localStorageService.clearJwt()
  },
  async getCalendlyUserLink ({ rootState }, { pmId }) {
    const res = await rootState.api.dataHub.getInvestorPmCalendlyLink({ platform_user_id: pmId })
    return res.calendly_link
  }
}

const getters = {
  isFeatureFlagEnabled: (state, _getters, rootState) => ({ featureFlagName }) => {
    if (!state.appConfigFeatureFlags) return false

    const flag = state.appConfigFeatureFlags[featureFlagName]

    // A flag absent from AppConfig is treated as off. Without the !flag guard
    // the property access throws and takes down whatever view read the flag.
    if (!flag || !flag.enabled) return false

    if (!Object.prototype.hasOwnProperty.call(flag, 'type')) {
      throw new Error(`"${featureFlagName}" is missing a 'type' attribute in AWS appconfig. Please review the appconfig configuration for this feature flag`)
    }

    if (!['boolean', 'firm_list', 'number', 'string', 'string_list'].includes(flag.type)) {
      throw new Error(`"${featureFlagName}" has an invalid 'type' attribute in AWS appconfig. Please review the appconfig configuration for this feature flag`)
    }

    // if flag is enabled and of type boolean, this will always be true
    if (flag.type === 'boolean') return true

    if (!Object.prototype.hasOwnProperty.call(flag, 'value')) {
      // all non-boolean flags should have a "value" attribute
      throw new Error(`"${featureFlagName}" is missing a 'value' attribute in AWS appconfig. Please review the appconfig configuration for this feature flag`)
    }

    if (flag.type === 'firm_list') {
      const firm = rootState?.context?.firm
      if (!firm) {
        throw new Error(`"${featureFlagName}" is of type 'firm_list' but no firm was provided. Please provide a firm name`)
      }
      return flag.value.includes(firm.toLowerCase())
    }

    if (['number', 'string', 'string_list'].includes(flag.type)) {
      return flag.value
    }
  },
  featureFlags: (s, getters) => {
    if (!s.appConfigFeatureFlags || typeof s.appConfigFeatureFlags !== 'object') { return {} }
    const features = {}
    Object.keys(s.appConfigFeatureFlags).forEach(key => {
      features[key] = getters.isFeatureFlagEnabled({ featureFlagName: key })
    })
    return features
  },
  opsEmail: (s) => s.opsEmail,
  isAnnualKycRequired: (s) => s.onboardingState?.survey === 'annual_kyc' && s.onboardingState?.survey_status !== 'completed',
  isActiveClient: (s) => s.userInfo?.investor_client_status && s.userInfo.investor_client_status === 'active',
  clientMaintenanceEnabled: (_s, getters) => getters.isFeatureFlagEnabled({ featureFlagName: 'client_maintenance' }),
  isIPV2Enabled: (_s, getters) => getters.isFeatureFlagEnabled({ featureFlagName: 'investor_portal_v2' }),
  // Flag name matches auth-hub, which reads the same AppConfig profile to pick
  // the Auth0 application an investor lands in (see authhub/sso_utils.py).
  isIPV3Enabled: (_s, getters) => getters.isFeatureFlagEnabled({ featureFlagName: 'investor_portal_v3_enabled' })
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
